import { useEffect, useRef, useState } from "react";
import type { PDFDocumentProxy } from "./pdf";

const THUMB_WIDTH = 148;

/** One render at a time so a long sheet index doesn't flood the worker. */
let chain: Promise<unknown> = Promise.resolve();
function enqueue<T>(job: () => Promise<T>): Promise<T> {
  const next = chain.then(job, job);
  chain = next.catch(() => undefined);
  return next;
}

/**
 * pdf.js thumbnail renders wait while the main sheet is rendering, so on a slow iPad the sheet you're
 * looking at always wins the worker. The Viewer flips this around each render.
 */
let mainBusy = false;
let idleWaiters: (() => void)[] = [];
export function setMainRendering(busy: boolean): void {
  mainBusy = busy;
  if (!busy) { const w = idleWaiters; idleWaiters = []; w.forEach((f) => f()); }
}
const mainIdle = () => (mainBusy ? new Promise<void>((r) => idleWaiters.push(r)) : Promise.resolve());

/**
 * Sheet thumbnail. With `documentId` it first shows the small server-rendered JPEG
 * (/api/documents/:id/thumbs/N.jpg); if that's missing it falls back to rendering with pdf.js.
 */
export function Thumb({ pdf, index, width = THUMB_WIDTH, documentId }: { pdf: PDFDocumentProxy; index: number; width?: number; documentId?: string }) {
  const holder = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [visible, setVisible] = useState(false);
  const [done, setDone] = useState(false);
  const [useImage, setUseImage] = useState(!!documentId);

  useEffect(() => {
    const el = holder.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) { setVisible(true); io.disconnect(); } }, { rootMargin: "300px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || done || useImage) return;
    let cancelled = false;
    enqueue(async () => {
      await mainIdle();
      if (cancelled || !canvas.current) return;
      const page = await pdf.getPage(index + 1);
      try {
        const base = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: width / base.width });
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const c = canvas.current;
        if (!c || cancelled) return;
        c.width = Math.floor(viewport.width * dpr);
        c.height = Math.floor(viewport.height * dpr);
        c.style.width = `${viewport.width}px`;
        c.style.height = `${viewport.height}px`;
        await page.render({ canvasContext: c.getContext("2d")!, viewport, transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined }).promise;
        if (!cancelled) setDone(true);
      } finally { page.cleanup(); }
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [visible, done, useImage, pdf, index, width]);

  return (
    <div ref={holder} className={`thumb ${done ? "ready" : ""}`}>
      {useImage
        ? visible && <img src={`/api/documents/${documentId}/thumbs/${index + 1}.jpg`} alt="" width={width} decoding="async" onLoad={() => setDone(true)} onError={() => setUseImage(false)} />
        : <canvas ref={canvas} />}
    </div>
  );
}
